import React, { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { getApi } from "../../../Services/apiService";
import {
  getMemberAgeLabel,
  getMemberBirthCalendarParts,
} from "../../../Utils/family-tree/dateAndTime";
import { getFamilyTreeIdFromStorage } from "../../../Utils/headOfLineRole";
import {
  BIRTHDAYS_TODAY_DISMISSED_KEY,
  fetchBirthByDay,
} from "../../../Utils/dashboardBirthByDay";
import {
  formatDashboardMemberDisplayName,
  isDashboardDeceasedMember,
  isDashboardInLawMember,
} from "../../../Utils/dashboardMemberDisplayName";
import { isExplicitDashboardStartOrTopOfLineMember } from "../../../Utils/dashboardStartOfLineMember";
import { extractAgeMilestoneGroups } from "../../../Utils/dashboardMonthlyAgeMilestones";
import {
  DashboardDeceasedAngel,
  DashboardFamilyLineColorLink,
  DashboardInLawAsterisk,
} from "./DashboardPhotoViewer";
import "../../../popups.css";

/** Today on the reader's own calendar — the day the popup is keyed to. */
function getTodayKey(date = new Date()) {
  const pad = (value) => String(value).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

function readDismissed() {
  try {
    return localStorage.getItem(BIRTHDAYS_TODAY_DISMISSED_KEY) || "";
  } catch (_) {
    return "";
  }
}

function writeDismissed(value) {
  try {
    localStorage.setItem(BIRTHDAYS_TODAY_DISMISSED_KEY, value);
  } catch (_) {
    /* private mode — the popup just comes back next visit */
  }
}

/** Birth month (1–12) off the row, else read off its date of birth. */
function getBirthMonth(member) {
  const month = parseInt(member?.birth_month, 10);
  if (Number.isFinite(month)) return month;
  const parts = getMemberBirthCalendarParts(member);
  return parts ? parts.month + 1 : null;
}

/**
 * The age a member turns today, for the "(age)" after a descendant's name: the
 * years since the birth year, or the months label for a baby born this year.
 * Null without a year of birth.
 */
function getAgeTurningToday(member, today = new Date()) {
  const year = getMemberBirthCalendarParts(member)?.year;
  if (!Number.isFinite(year)) return null;
  const years = today.getFullYear() - year;
  return years === 0 ? getMemberAgeLabel(member, 0) : years;
}

/**
 * The words under a milestone age: "A Year around the sun", "2 Decades around
 * the sun", "2 Decades and a half around the sun", "A Century around the sun".
 * Ages that are no round number of decades count in years (5, 7, 16, 18, 21, 62).
 */
export function getMilestoneAgePhrase(age) {
  const decades = (count) => (count === 1 ? "A Decade" : `${count} Decades`);
  let span;
  if (age === 1) span = "A Year";
  else if (age === 100) span = "A Century";
  else if (age > 100) {
    const extra = age - 100;
    span = `A Century and ${extra % 10 === 0 ? decades(extra / 10).replace(/^A /, "a ") : `${extra} Years`}`;
  } else if (age % 10 === 0) span = decades(age / 10);
  else if (age % 10 === 5 && age >= 15) span = `${decades(Math.floor(age / 10))} and a half`;
  else span = `${age} Years`;
  return `${span} around the sun`;
}

/**
 * This month's milestone birthdays — the rows Monthly Milestone Ages lists for
 * the current month — each with the age reached, youngest milestone first and
 * names A–Z within an age.
 */
function getMilestonesThisMonth(data, monthIndex) {
  return extractAgeMilestoneGroups(data)
    .flatMap((group) =>
      (group.details || [])
        .filter((member) => getMemberBirthCalendarParts(member)?.month === monthIndex)
        .map((member) => ({ member, age: Number(group.milestone) }))
    )
    .sort(
      (a, b) =>
        a.age - b.age ||
        formatDashboardMemberDisplayName(a.member).localeCompare(
          formatDashboardMemberDisplayName(b.member)
        )
    );
}

/**
 * "Birthday Today" popup on the dashboard: everyone in the Birth Day report
 * (/report-management/birth-by-day — the same call the report makes) whose
 * birthday is today, by name and family line.
 *
 * Shown once per day per family tree. Only the close buttons dismiss it — a
 * click on the backdrop or Escape does nothing — and closing it keeps it shut
 * for the rest of the day; the next day it opens again for that day's
 * birthdays. On a day with no birthdays it still opens for the month's
 * milestone ages; nothing shows only when there are neither.
 *
 * Under the birthdays it lists the month's milestone ages (see
 * getMilestonesThisMonth), by name, age, line and the years they mark.
 *
 * @param {object} props
 * @param {object} props.headsMap — the dashboard's heads-of-line map, so each
 *   line names its head as the dashboard's lists do
 */
function BirthdaysTodayModal({ headsMap }) {
  const [members, setMembers] = useState([]);
  const [milestones, setMilestones] = useState([]);
  const [dismissKey, setDismissKey] = useState("");

  const load = useCallback(async () => {
    const familyTreeId = getFamilyTreeIdFromStorage();
    const key = `${getTodayKey()}|${familyTreeId || ""}`;
    setDismissKey(key);
    if (!familyTreeId || readDismissed() === key) {
      setMembers([]);
      setMilestones([]);
      return;
    }

    try {
      const today = new Date();
      // The milestones are extra: if their call fails the birthdays still show.
      const [report, statistics] = await Promise.all([
        fetchBirthByDay(familyTreeId),
        getApi(`/dashboard-statistics-with-details?family_tree_id=${familyTreeId}`).catch(
          (error) => {
            console.log("error fetching this month's milestone ages:", error);
            return null;
          }
        ),
      ]);
      const day = report.days.find((entry) => entry.day === today.getDate());
      const birthdays = (day?.members || [])
        .filter((member) => getBirthMonth(member) === today.getMonth() + 1)
        .map((member) => ({
          ...member,
          is_in_laws:
            member?.is_in_laws ?? member?.is_in_law ?? member?.is_spouse ?? false,
        }));
      // The tree may have changed while the call was out.
      if (readDismissed() === key || getFamilyTreeIdFromStorage() !== familyTreeId) return;
      setMembers(birthdays);
      setMilestones(
        statistics?.statusCode === 200 && statistics?.data
          ? getMilestonesThisMonth(statistics.data, today.getMonth())
          : []
      );
    } catch (error) {
      console.log("error fetching today's birthdays:", error);
      setMembers([]);
      setMilestones([]);
    }
  }, []);

  useEffect(() => {
    load();
    // Switching family tree (side picker, tree switcher) re-asks for that tree.
    window.addEventListener("localStorageChange", load);
    return () => window.removeEventListener("localStorageChange", load);
  }, [load]);

  const close = () => {
    writeDismissed(dismissKey);
    setMembers([]);
    setMilestones([]);
  };

  if (!members.length && !milestones.length) return null;

  return createPortal(
    // No onClick on the backdrop: only the close buttons dismiss the popup.
    <div className="sidebar-notice-overlay birthdays-today-overlay" role="presentation">
      <div
        className="sidebar-notice-modal birthdays-today-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="birthdays-today-title"
      >
        <button
          type="button"
          className="sidebar-notice-close"
          onClick={close}
          aria-label="Close"
        >
          ×
        </button>
        <h3 className="sidebar-notice-title birthdays-today-title" id="birthdays-today-title">
          🎂 Birthday(s) Today
        </h3>
        {!members.length && <p className="birthdays-today-none">No birthdays today.</p>}
        <ul className="birthdays-today-list">
          {members.map((member, index) => {
            const isInLaw = isDashboardInLawMember(member);
            // Ages for descendants only — no in-law shows one.
            const age = isInLaw ? null : getAgeTurningToday(member);
            return (
            <li key={member?.id || index} className="birthdays-today-row">
              <span className="birthdays-today-name">
                {formatDashboardMemberDisplayName(member)}
                {isInLaw && !isExplicitDashboardStartOrTopOfLineMember(member) && (
                  <DashboardInLawAsterisk />
                )}
                {isDashboardDeceasedMember(member) && <DashboardDeceasedAngel />}
                {age != null && <span className="birthdays-today-milestone-age">({age})</span>}
              </span>
              <span className="birthdays-today-line">
                {/* Line only, no head name or dot — the same as the milestone rows. */}
                {member?.color_name || member?.family_line_color ? (
                  <DashboardFamilyLineColorLink
                    hexCode={member?.hex_code || member?.family_line_hex_code}
                    colorName={`${member?.color_name || member?.family_line_color} Line`}
                    member={member}
                    headsMap={headsMap}
                    showDot={false}
                  />
                ) : null}
              </span>
            </li>
            );
          })}
        </ul>
        {milestones.length > 0 && (
          <>
            <h4 className="birthdays-today-milestones-title">
              Milestone Ages for {new Date().toLocaleString("en-US", { month: "long" })}
            </h4>
            <ul className="birthdays-today-list birthdays-today-milestones-list">
              {milestones.map(({ member, age }, index) => (
                <li
                  key={`${member?.id || index}-${age}`}
                  className="birthdays-today-row birthdays-today-milestone-row"
                >
                  <span className="birthdays-today-name">
                    {formatDashboardMemberDisplayName(member)}
                    {isDashboardDeceasedMember(member) && <DashboardDeceasedAngel />}
                    <span className="birthdays-today-milestone-age">({age})</span>
                  </span>
                  <span className="birthdays-today-line">
                    {member?.color_name || member?.family_line_color ? (
                      <DashboardFamilyLineColorLink
                        hexCode={member?.family_line_hex_code || member?.hex_code}
                        colorName={`${member?.family_line_color || member?.color_name} Line`}
                        member={member}
                        headsMap={headsMap}
                        showDot={false}
                      />
                    ) : null}
                  </span>
                  <span className="birthdays-today-milestone-phrase">
                    {getMilestoneAgePhrase(age)}
                  </span>
                </li>
              ))}
            </ul>
          </>
        )}
        <button type="button" className="sidebar-notice-btn" onClick={close}>
          Close
        </button>
      </div>
    </div>,
    document.body
  );
}

export default BirthdaysTodayModal;
